import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import greeningApi, { GreeningAnalysis, GreeningQuestionItem } from '../services/greeningApi';

const POLL_MS = 4000;
const ACTIVE_STATUSES = new Set(['pending', 'processing', 'retrying']);

export function useGreeningAnalysis(legislationId: number | null | undefined, enabled: boolean) {
  const queryClient = useQueryClient();
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const analysisQuery = useQuery({
    queryKey: ['greening-analysis', legislationId],
    queryFn: () => greeningApi.getAnalysis(legislationId!),
    enabled: Boolean(enabled && legislationId),
    staleTime: 15_000,
    retry: 1,
  });

  const historyQuery = useQuery({
    queryKey: ['greening-history', legislationId],
    queryFn: () => greeningApi.getHistory(legislationId!),
    enabled: Boolean(enabled && legislationId),
    staleTime: 10_000,
    // Enquanto houver pergunta sendo processada, re-busca a cada 2s: a resposta
    // aparece sozinha no chat, sem precisar fechar/reabrir (o LLM pode demorar/retry).
    refetchInterval: (data) =>
      (data ?? []).some((item) => item.status === 'pending' || item.status === 'processing' || item.status === 'retrying')
        ? 2000
        : false,
  });

  const refreshAll = useCallback(() => {
    if (!legislationId) return;
    queryClient.invalidateQueries({ queryKey: ['greening-analysis', legislationId] });
    queryClient.invalidateQueries({ queryKey: ['greening-history', legislationId] });
  }, [legislationId, queryClient]);

  const [asking, setAsking] = useState(false);

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!enabled || !legislationId) return;
    const status = analysisQuery.data?.status;
    const fiveWhysStatus = analysisQuery.data?.five_whys_status;
    const historyPending = (historyQuery.data || []).some(
      (h) => h.status === 'pending' || h.status === 'processing' || h.status === 'retrying',
    );
    const shouldPoll =
      (status && ACTIVE_STATUSES.has(status)) ||
      (fiveWhysStatus && ACTIVE_STATUSES.has(String(fiveWhysStatus))) ||
      historyPending ||
      asking;
    if (shouldPoll) {
      pollRef.current = setInterval(refreshAll, POLL_MS);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [
    enabled,
    legislationId,
    analysisQuery.data?.status,
    analysisQuery.data?.five_whys_status,
    historyQuery.data,
    asking,
    refreshAll,
  ]);

  const askQuestion = useCallback(
    async (question: string) => {
      if (!legislationId || !question.trim()) return;
      setAsking(true);
      try {
        const result = await greeningApi.ask(legislationId, question.trim());
        if (result.status === 'completed' || result.status === 'rate_limited') {
          refreshAll();
          return;
        }
        await new Promise((r) => setTimeout(r, 1500));
        refreshAll();
      } finally {
        setAsking(false);
      }
    },
    [legislationId, refreshAll],
  );

  return {
    analysis: analysisQuery.data,
    analysisLoading: analysisQuery.isLoading,
    analysisError: analysisQuery.error,
    history: historyQuery.data || [],
    historyLoading: historyQuery.isLoading,
    askQuestion,
    asking,
    refreshAll,
    legislationId: legislationId ?? null,
  };
}

export type UseGreeningAnalysisResult = ReturnType<typeof useGreeningAnalysis>;
